import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { M14_ROLES, hasAnyRole } from '../../lib/roles';
import type { EnforcementCase, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, InfoNote, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function Enforcement() {
  const { persona } = useAuth();
  const canView = hasAnyRole(persona?.roles, M14_ROLES.ENFORCEMENT_OFFICER, M14_ROLES.SENIOR_LO);
  const [status, setStatus] = useState('');

  const query = new URLSearchParams({ limit: '50' });
  if (status) query.set('status', status);
  const listQ = useApiQuery<Page<EnforcementCase>>(canView ? `/api/v1/m14/enforcement?${query.toString()}` : null);

  return (
    <div>
      <PageHeader title="Enforcement" subtitle="Show-cause notices, suspensions, revocations, and enforcement appeals." />
      {!canView ? (
        <InfoNote message="Case listing requires an Enforcement Officer or Senior Licensing Officer role. If you've received a show-cause notice, use the link SeyNotify sent you to open your case directly." />
      ) : (
        <>
          <Field label="Status" className="mb-4 max-w-xs">
            <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              {['OPEN', 'SHOW_CAUSE_ISSUED', 'DECIDED', 'APPEALED', 'CLOSED'].map((s) => <option key={s} value={s}>{s.replaceAll('_', ' ')}</option>)}
            </select>
          </Field>
          {listQ.error ? <ErrorNote message={listQ.error} /> : null}
          {listQ.loading ? <Spinner /> : (listQ.data?.items.length ?? 0) === 0 ? <EmptyState title="No enforcement cases" /> : (
            <div className="space-y-2">
              {listQ.data!.items.map((c) => (
                <Link key={c.id} to={`/licensing/enforcement/${c.id}`} className="card block p-4 hover:border-brand-300">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{c.breachDescription}</span>
                    <StatusBadge status={c.state} />
                  </div>
                  <div className="mt-1 font-mono text-xs text-slate-400">licence {c.licenceId}</div>
                </Link>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
